/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useCallback, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Portal,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AccountTree as AccountTreeIcon,
  CalendarMonth as CalendarIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CloseFullscreen as CloseFullscreenIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Forum as SourceMessageIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CallMerge as MergeIcon,
  OpenInFull as OpenInFullIcon,
  Schedule as ScheduleIcon,
  CallSplit as SplitIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { expertApi } from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import { AISystem } from '../../types';
import type {
  BuilderArtifactWorkflowArtifact,
  BuilderIntentItemPatchRequest,
  BuilderIntentLedger,
  BuilderIntentLedgerEventRequest,
} from '../../types/agent';
import { AgentSchedulePanel } from './AgentSchedulePanel';
import AISystemCanvas from './AISystemCanvas';
import BuilderSuggestedReplyCard from './BuilderSuggestedReplyCard';
import type {
  BuilderPlanActionView,
  BuilderPlanDiffView,
  BuilderPlanDetailView,
  BuilderPlanHealthView,
  BuilderPlanPreviewView,
  BuilderPlanRevisionView,
  BuilderPlanReviewView,
  BuilderPlanStatusView,
} from './aiBuilderPlanStatus';
import { SchedulePanel } from './SchedulePanel';
import { ScheduleExecutionsScreen } from './ScheduleExecutionsScreen';

interface AIBuilderInsightsPanelProps {
  system: AISystem;
  isStaff?: boolean;
  sessionData?: any;
  isHistoricalSession?: boolean;
  selectedMessageId?: string | null;
  builderPlanStatusView?: BuilderPlanStatusView | null;
  builderPlanDetailView?: BuilderPlanDetailView | null;
  builderPlanReviewView?: BuilderPlanReviewView | null;
  builderPlanActionView?: BuilderPlanActionView | null;
  builderPlanPreviewView?: BuilderPlanPreviewView | null;
  builderPlanHealthView?: BuilderPlanHealthView | null;
  builderPlanDiffView?: BuilderPlanDiffView | null;
  builderPlanRevisionView?: BuilderPlanRevisionView | null;
  builderIntentLedger?: BuilderIntentLedger | null;
  isLoadingBuilderIntentLedger?: boolean;
  activeBuilderArtifact?: BuilderArtifactWorkflowArtifact | null;
  isLoadingActiveBuilderArtifact?: boolean;
  onApproveActiveBuilderArtifact?: () => Promise<void> | void;
  onApplyActiveBuilderArtifact?: () => Promise<void> | void;
  activeBuilderArtifactAction?: 'approve' | 'apply' | null;
  onUpdateBuilderIntentItem?: (ledgerId: string, itemId: string, patch: BuilderIntentItemPatchRequest) => Promise<void> | void;
  onAppendBuilderIntentLedgerEvent?: (ledgerId: string, event: BuilderIntentLedgerEventRequest, actionItemId?: string) => Promise<void> | void;
  onSelectSourceMessage?: (sourceMessageId: string) => void;
  onUseSuggestedReply?: (message: string) => void;
  builderIntentLedgerNotice?: string | null;
  updatingBuilderIntentItemId?: string | null;
  nodes?: Node[];
  edges?: Edge[];
  onNodesChange?: any;
  onEdgesChange?: any;
  onConnect?: any;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onAutoLayout?: () => void;
  expertsData?: any;
  addExpertToCanvas?: (expert: any) => void;
  setExpertFormOpen?: (open: boolean) => void;
  selectedNode?: Node | null;
  setSelectedNode?: (node: Node | null) => void;
  setNodes?: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<Edge[]>>;
  refetchExperts?: () => void;
  validationIssues?: any[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, any>;
  onViewExpertRun?: (expertId: number) => void;
  nodeTypes?: any;
  onExpertUpdate?: (expert: any | null) => void;
  onExitSnapshot?: () => void;
  isMobile?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ height: '100%', overflow: 'auto', minWidth: 0, width: '100%' }}
    >
      {value === index && (
        <Box sx={{ pt: { xs: 0.5, sm: 1 }, px: 0, pb: 0, minWidth: 0, width: '100%', height: '100%' }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

type IntentEditDraft = {
  label: string;
  description: string;
  pinned_definition: string;
};

type IntentSplitDraft = {
  firstLabel: string;
  firstDescription: string;
  secondLabel: string;
  secondDescription: string;
};

type BuilderClarificationCard = {
  clarification_id: string;
  item_id: string;
  stable_key?: string;
  item_label?: string;
  term_key: string;
  term_label: string;
  prompt: string;
  default_definition: string;
};

const formatReviewToken = (value: unknown, fallback = 'Not set') => {
  const text = String(value ?? '').trim();
  if (!text) return fallback;
  return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  architect_handoff_truncated: 'Design exceeded model output limit',
};

const formatWorkflowStatusLabel = (value: unknown, fallback = 'Not set') => {
  const text = String(value ?? '').trim();
  if (!text) return fallback;
  return WORKFLOW_STATUS_LABELS[text] ?? formatReviewToken(text);
};

const summarizeRuntimeSnapshot = (value: unknown, fallback: string) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fallback;
  const entries = Object.entries(value as Record<string, any>)
    .slice(0, 3)
    .map(([key, item]) => {
      if (item === null || item === undefined || item === '') return null;
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        return `${formatReviewToken(key)}: ${item}`;
      }
      return `${formatReviewToken(key)} captured`;
    })
    .filter(Boolean);
  return entries.length > 0 ? entries.join('; ') : fallback;
};

const normalizeArchitectText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const text = value.trim();
    return text || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, any>;
  for (const key of ['label', 'title', 'question', 'prompt', 'text', 'description', 'summary', 'value']) {
    const candidate = normalizeArchitectText(record[key]);
    if (candidate) {
      return candidate;
    }
  }

  return null;
};

const toArchitectTextList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeArchitectText(item))
      .filter((item): item is string => Boolean(item));
  }

  const single = normalizeArchitectText(value);
  return single ? [single] : [];
};

const summarizeArchitectExpertPrompt = (value: unknown): string | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, any>;
  for (const key of [
    'expert_prompt',
    'expert_prompt_content',
    'expert_prompt_instructions',
    'prompt_instructions',
    'role_instructions',
    'proposal_notes',
  ]) {
    const candidate = normalizeArchitectText(record[key]);
    if (candidate) {
      return candidate;
    }
  }

  return null;
};

const CanvasResizeFitView: React.FC<{ isFullscreen: boolean; nodeCount: number }> = ({ isFullscreen, nodeCount }) => {
  const { fitView } = useReactFlow();

  React.useEffect(() => {
    if (nodeCount === 0) return;

    const timer = window.setTimeout(() => {
      fitView({ padding: 0.25, maxZoom: 1 });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [fitView, isFullscreen, nodeCount]);

  return null;
};

const AIBuilderInsightsPanel: React.FC<AIBuilderInsightsPanelProps> = ({
  system,
  isStaff = false,
  sessionData,
  selectedMessageId = null,
  builderPlanStatusView = null,
  builderPlanDetailView = null,
  builderPlanReviewView = null,
  builderPlanActionView = null,
  builderPlanPreviewView = null,
  builderPlanHealthView = null,
  builderPlanDiffView = null,
  builderPlanRevisionView = null,
  builderIntentLedger = null,
  isLoadingBuilderIntentLedger = false,
  activeBuilderArtifact = null,
  isLoadingActiveBuilderArtifact = false,
  onApproveActiveBuilderArtifact,
  onApplyActiveBuilderArtifact,
  activeBuilderArtifactAction = null,
  onUpdateBuilderIntentItem,
  onAppendBuilderIntentLedgerEvent,
  onSelectSourceMessage,
  onUseSuggestedReply,
  builderIntentLedgerNotice = null,
  updatingBuilderIntentItemId = null,
  nodes: canvasNodes = [],
  edges: canvasEdges = [],
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData: canvasExpertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  setSelectedNode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues = [],
  onSaveCanvas,
  isSavingCanvas = false,
  onRunCanvas,
  isRunningCanvas = false,
  canRunCanvas,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  onExitSnapshot,
  isMobile = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [showArtifactTechnicalDetails, setShowArtifactTechnicalDetails] = useState(false);
  const [forceLiveView, setForceLiveView] = useState(false);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const [editingIntentItemId, setEditingIntentItemId] = useState<string | null>(null);
  const [intentEditDraft, setIntentEditDraft] = useState<IntentEditDraft>({
    label: '',
    description: '',
    pinned_definition: '',
  });
  const [splittingIntentItemId, setSplittingIntentItemId] = useState<string | null>(null);
  const [confirmingRemovalIntentItemId, setConfirmingRemovalIntentItemId] = useState<string | null>(null);
  const [editingClarificationId, setEditingClarificationId] = useState<string | null>(null);
  const [clarificationEditDraft, setClarificationEditDraft] = useState('');
  const [intentSplitDraft, setIntentSplitDraft] = useState<IntentSplitDraft>({
    firstLabel: '',
    firstDescription: '',
    secondLabel: '',
    secondDescription: '',
  });

  const hasValidSnapshot =
    !!sessionData?.system_snapshot &&
    Array.isArray(sessionData.system_snapshot.experts) &&
    sessionData.system_snapshot.experts.length > 0;

  const shouldUseSnapshot = selectedMessageId !== null && hasValidSnapshot && !forceLiveView;

  React.useEffect(() => {
    setForceLiveView(false);
  }, [sessionData?.id, selectedMessageId]);

  const hasInteractiveCanvasProps = Boolean(
    onNodesChange &&
      onEdgesChange &&
      onConnect &&
      onNodeClick &&
      onAutoLayout &&
      addExpertToCanvas &&
      setExpertFormOpen &&
      setSelectedNode &&
      setNodes &&
      setEdges &&
      refetchExperts
  );

  const canUseInteractiveCanvas = !shouldUseSnapshot && hasInteractiveCanvasProps;

  const { data: expertsData } = useQuery({
    queryKey: ['experts', system.id, 'preview-fallback'],
    queryFn: () => expertApi.list({ ai_system: system.id }),
    enabled: !canUseInteractiveCanvas && !shouldUseSnapshot,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const snapshotSystem = shouldUseSnapshot ? sessionData.system_snapshot.system : null;
  const snapshotExperts = shouldUseSnapshot ? sessionData.system_snapshot.experts : null;
  const experts = shouldUseSnapshot ? snapshotExperts : (expertsData?.results || []);
  const displaySystem = shouldUseSnapshot ? snapshotSystem : system;
  const activeIntentItems = builderIntentLedger?.items?.filter((item) => !item.is_removed) ?? [];
  const artifactReviewRows = activeBuilderArtifact?.review_payload?.rows ?? [];
  const artifactReviewSummary = activeBuilderArtifact?.review_payload?.summary_sentence || null;
  const artifactActions = activeBuilderArtifact?.allowed_next_actions ?? {};
  const artifactApproveAction = (artifactActions.approve ?? {}) as { allowed?: boolean; reason?: string | null };
  const artifactApplyAction = (artifactActions.apply ?? {}) as { allowed?: boolean; reason?: string | null };
  const artifactWorkflowMode = activeBuilderArtifact?.review_payload?.workflow_mode || activeBuilderArtifact?.workflow_approval_policy?.workflow_mode || null;
  const architectDesignPackage =
    activeBuilderArtifact?.design_package &&
    typeof activeBuilderArtifact.design_package === 'object' &&
    !Array.isArray(activeBuilderArtifact.design_package)
      ? activeBuilderArtifact.design_package
      : null;
  const architectObjective = normalizeArchitectText(architectDesignPackage?.objective);
  const architectDesignSource = String(
    activeBuilderArtifact?.design_package_source || architectDesignPackage?.source || ''
  ).trim();
  const architectDesignMode = String(architectDesignPackage?.design_mode || '').trim();
  const architectUpdateType = String(
    activeBuilderArtifact?.update_contract?.update_type
      || architectDesignPackage?.update_type
      || activeBuilderArtifact?.mutation_plan?.update_type
      || architectDesignPackage?.mutation_plan?.update_type
      || ''
  ).trim().toLowerCase();
  const isScopedArchitectUpdate = Boolean(
    architectDesignMode === 'existing_system_update'
      && architectUpdateType
      && architectUpdateType !== 'structural_redesign'
  );
  const approveArtifactLabel = isScopedArchitectUpdate ? 'Approve update' : 'Approve and build';
  const approvingArtifactLabel = isScopedArchitectUpdate ? 'Approving and applying...' : 'Approving and building...';
  const applyArtifactLabel = isScopedArchitectUpdate ? 'Apply update' : 'Apply plan';
  const architectRequestedMutations = toArchitectTextList(
    activeBuilderArtifact?.requested_mutations || architectDesignPackage?.requested_mutations
  );
  const architectOpenQuestions = toArchitectTextList(architectDesignPackage?.open_questions);
  const architectOpenQuestionReply = architectOpenQuestions.length
    ? [
        'Please revise the plan using these answers:',
        '',
        ...architectOpenQuestions.map((question) => `- ${question}: `),
      ].join('\n')
    : '';
  const hasUnresolvedArchitectQuestions = architectOpenQuestions.length > 0;
  const visibleArtifactReviewRows = showArtifactTechnicalDetails
    ? artifactReviewRows
    : artifactReviewRows.filter((row) => {
        const status = String(row.status || 'unknown');
        const impact = String(row.approval_impact || '');
        return impact === 'blocks_approval' || status === 'blocked' || status === 'missing' || status === 'unsupported';
      });
  const architectUserConstraints = toArchitectTextList(architectDesignPackage?.user_constraints);
  const architectExpertDesigns = Array.isArray(architectDesignPackage?.expert_designs)
    ? architectDesignPackage.expert_designs.filter(
        (item): item is Record<string, any> => Boolean(item) && typeof item === 'object' && !Array.isArray(item)
      )
    : [];
  const architectScopeSummary = summarizeRuntimeSnapshot(
    architectDesignPackage?.scope,
    'No explicit scope captured.'
  );
  const architectBuildBriefSummary = summarizeRuntimeSnapshot(
    architectDesignPackage?.build_brief,
    'No build brief captured.'
  );
  const architectMutationPlanSummary = summarizeRuntimeSnapshot(
    activeBuilderArtifact?.mutation_plan || architectDesignPackage?.mutation_plan,
    'No mutation plan captured.'
  );
  const runtimeStatus = activeBuilderArtifact?.runtime_status ?? null;
  const runtimeSummary = (runtimeStatus?.summary ?? {}) as Record<string, any>;
  const runtimeRuns = Array.isArray(runtimeStatus?.latest_runs) ? runtimeStatus.latest_runs : [];
  const durableGraphBuild = activeBuilderArtifact?.durable_graph_build ?? null;
  const durableWrapperTools = Array.isArray(durableGraphBuild?.wrapper_tools)
    ? durableGraphBuild.wrapper_tools
    : [];
  const refinementReview = activeBuilderArtifact?.refinement_review ?? null;
  const refinementSummary = (refinementReview?.summary ?? {}) as Record<string, any>;
  const refinementProposals = Array.isArray(refinementReview?.proposals) ? refinementReview.proposals : [];
  const activeIntentItemIds = new Set(activeIntentItems.map((item) => item.id));
  const answeredClarificationIds = new Set(
    (builderIntentLedger?.events ?? [])
      .filter((event) => ['clarification_accepted', 'clarification_edited', 'clarification_rejected'].includes(String(event.event_type)))
      .map((event) => {
        const card = event.payload?.clarification_card ?? {};
        return String(card.clarification_id || `${card.item_id || event.item_id}:${card.term_key || ''}`);
      })
  );
  const clarificationCards: BuilderClarificationCard[] = (builderIntentLedger?.events ?? [])
    .filter((event) => String(event.event_type) === 'clarification_offered')
    .map((event) => event.payload?.clarification_card as BuilderClarificationCard | undefined)
    .filter((card): card is BuilderClarificationCard => Boolean(
      card?.clarification_id &&
      card.item_id &&
      card.term_key &&
      card.default_definition &&
      activeIntentItemIds.has(card.item_id) &&
      !answeredClarificationIds.has(card.clarification_id)
    ));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const startEditingIntentItem = useCallback((item: BuilderIntentLedger['items'][number]) => {
    setSplittingIntentItemId(null);
    setConfirmingRemovalIntentItemId(null);
    setEditingIntentItemId(item.id);
    setIntentEditDraft({
      label: item.label || '',
      description: item.description || '',
      pinned_definition: item.pinned_definition || '',
    });
  }, []);

  const cancelIntentEdit = useCallback(() => {
    setEditingIntentItemId(null);
    setIntentEditDraft({ label: '', description: '', pinned_definition: '' });
  }, []);

  const updateIntentEditDraft = useCallback((field: keyof IntentEditDraft, value: string) => {
    setIntentEditDraft((draft) => ({ ...draft, [field]: value }));
  }, []);

  const submitIntentEdit = useCallback(async () => {
    if (!builderIntentLedger || !editingIntentItemId || !onUpdateBuilderIntentItem) {
      return;
    }
    const label = intentEditDraft.label.trim();
    if (!label) {
      return;
    }
    await onUpdateBuilderIntentItem(builderIntentLedger.id, editingIntentItemId, {
      ledger_version: builderIntentLedger.ledger_version,
      label,
      description: intentEditDraft.description,
      pinned_definition: intentEditDraft.pinned_definition,
    });
    cancelIntentEdit();
  }, [builderIntentLedger, cancelIntentEdit, editingIntentItemId, intentEditDraft, onUpdateBuilderIntentItem]);

  const updateIntentSplitDraft = useCallback((field: keyof IntentSplitDraft, value: string) => {
    setIntentSplitDraft((draft) => ({ ...draft, [field]: value }));
  }, []);

  const startSplittingIntentItem = useCallback((item: BuilderIntentLedger['items'][number]) => {
    setEditingIntentItemId(null);
    setConfirmingRemovalIntentItemId(null);
    setSplittingIntentItemId(item.id);
    setIntentSplitDraft({
      firstLabel: item.label || '',
      firstDescription: item.description || '',
      secondLabel: '',
      secondDescription: '',
    });
  }, []);

  const cancelIntentSplit = useCallback(() => {
    setSplittingIntentItemId(null);
    setIntentSplitDraft({
      firstLabel: '',
      firstDescription: '',
      secondLabel: '',
      secondDescription: '',
    });
  }, []);

  const startConfirmingIntentRemoval = useCallback((itemId: string) => {
    setEditingIntentItemId(null);
    setSplittingIntentItemId(null);
    setConfirmingRemovalIntentItemId(itemId);
  }, []);

  const cancelIntentRemoval = useCallback(() => {
    setConfirmingRemovalIntentItemId(null);
  }, []);

  const appendIntentLedgerEvent = useCallback(async (
    event: Omit<BuilderIntentLedgerEventRequest, 'ledger_version'>,
    actionItemId?: string,
  ) => {
    if (!builderIntentLedger || !onAppendBuilderIntentLedgerEvent) {
      return;
    }
    await onAppendBuilderIntentLedgerEvent(builderIntentLedger.id, {
      ledger_version: builderIntentLedger.ledger_version,
      ...event,
    }, actionItemId);
  }, [builderIntentLedger, onAppendBuilderIntentLedgerEvent]);

  const acceptClarificationCard = useCallback(async (card: BuilderClarificationCard) => {
    await appendIntentLedgerEvent({
      event_type: 'clarification_accepted',
      payload: { item_id: card.item_id, term_key: card.term_key },
    }, card.clarification_id);
  }, [appendIntentLedgerEvent]);

  const startEditingClarificationCard = useCallback((card: BuilderClarificationCard) => {
    setEditingClarificationId(card.clarification_id);
    setClarificationEditDraft(card.default_definition || '');
  }, []);

  const cancelClarificationEdit = useCallback(() => {
    setEditingClarificationId(null);
    setClarificationEditDraft('');
  }, []);

  const submitClarificationEdit = useCallback(async (card: BuilderClarificationCard) => {
    const definition = clarificationEditDraft.trim();
    if (!definition) {
      return;
    }
    await appendIntentLedgerEvent({
      event_type: 'clarification_edited',
      payload: { item_id: card.item_id, term_key: card.term_key, definition },
    }, card.clarification_id);
    cancelClarificationEdit();
  }, [appendIntentLedgerEvent, cancelClarificationEdit, clarificationEditDraft]);

  const rejectClarificationCard = useCallback(async (card: BuilderClarificationCard) => {
    await appendIntentLedgerEvent({
      event_type: 'clarification_rejected',
      payload: { item_id: card.item_id, term_key: card.term_key },
    }, card.clarification_id);
  }, [appendIntentLedgerEvent]);

  const reorderIntentItem = useCallback(async (itemId: string, direction: -1 | 1) => {
    const currentIndex = activeIntentItems.findIndex((item) => item.id === itemId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= activeIntentItems.length) {
      return;
    }
    const orderedItemIds = activeIntentItems.map((item) => item.id);
    const [movedItemId] = orderedItemIds.splice(currentIndex, 1);
    orderedItemIds.splice(nextIndex, 0, movedItemId);
    await appendIntentLedgerEvent({
      event_type: 'item_reordered',
      payload: { ordered_item_ids: orderedItemIds },
    }, itemId);
  }, [activeIntentItems, appendIntentLedgerEvent]);

  const submitIntentSplit = useCallback(async () => {
    if (!splittingIntentItemId) {
      return;
    }
    const firstLabel = intentSplitDraft.firstLabel.trim();
    const secondLabel = intentSplitDraft.secondLabel.trim();
    if (!firstLabel || !secondLabel) {
      return;
    }
    await appendIntentLedgerEvent({
      event_type: 'item_split',
      payload: {
        item_id: splittingIntentItemId,
        parts: [
          { label: firstLabel, description: intentSplitDraft.firstDescription },
          { label: secondLabel, description: intentSplitDraft.secondDescription },
        ],
      },
    }, splittingIntentItemId);
    cancelIntentSplit();
  }, [appendIntentLedgerEvent, cancelIntentSplit, intentSplitDraft, splittingIntentItemId]);

  const mergeIntentItemWithNext = useCallback(async (itemIndex: number) => {
    const item = activeIntentItems[itemIndex];
    const nextItem = activeIntentItems[itemIndex + 1];
    if (!item || !nextItem) {
      return;
    }
    await appendIntentLedgerEvent({
      event_type: 'item_merged',
      payload: {
        item_ids: [item.id, nextItem.id],
        label: `${item.label || item.stable_key} + ${nextItem.label || nextItem.stable_key}`,
        description: [item.description, nextItem.description].filter(Boolean).join('\n\n'),
      },
    }, item.id);
  }, [activeIntentItems, appendIntentLedgerEvent]);

  const removeIntentItem = useCallback(async (itemId: string) => {
    await appendIntentLedgerEvent({
      event_type: 'item_removed_with_confirmation',
      payload: { item_id: itemId, confirmed: true },
    }, itemId);
    cancelIntentRemoval();
  }, [appendIntentLedgerEvent, cancelIntentRemoval]);

  React.useEffect(() => {
    if (!isCanvasFullscreen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCanvasFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCanvasFullscreen]);

  const buildFlowData = useCallback(() => {
    const nodes: Node[] = experts.map((expert: any, index: number) => ({
      id: expert.id.toString(),
      type: 'default',
      position: {
        x: displaySystem?.config?.expert_positions?.[expert.id]?.x ?? expert.position_x ?? (index % 3) * 280,
        y: displaySystem?.config?.expert_positions?.[expert.id]?.y ?? expert.position_y ?? Math.floor(index / 3) * 180,
      },
      data: {
        label: (
          <Box sx={{ textAlign: 'center', p: 1, maxWidth: 240 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.2,
                minHeight: '2.4em',
              }}
            >
              {expert.name}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
              {expert.expert_type}
            </Typography>
            {expert.model?.name && (
              <Typography
                variant="caption"
                display="block"
                sx={{
                  fontSize: '0.65rem',
                  color: 'info.main',
                  mt: 0.25,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {expert.model.name}
              </Typography>
            )}
          </Box>
        ),
        isDark,
      },
      style: {
        background: expert.status === 'ACTIVE' ? (isDark ? '#1e3a5f' : '#e3f2fd') : (isDark ? '#2d2d2d' : '#f5f5f5'),
        border: `2px solid ${expert.status === 'ACTIVE' ? '#2196f3' : '#9e9e9e'}`,
        borderRadius: 12,
        padding: 4,
        width: 240,
        minHeight: 100,
        color: isDark ? '#fff' : 'inherit',
      },
    }));

    const edges: Edge[] = [];

    experts.forEach((expert: any) => {
      if (expert.connections && Array.isArray(expert.connections)) {
        expert.connections.forEach((targetId: number) => {
          edges.push({
            id: `e-${expert.id}-${targetId}`,
            source: expert.id.toString(),
            target: targetId.toString(),
            type: 'smoothstep',
            animated: true,
            label: 'depends on',
            style: { stroke: '#2196f3', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#2196f3',
              width: 20,
              height: 20,
            },
          });
        });
      }
    });

    const systemConnections = displaySystem?.config?.expert_connections;
    if (systemConnections && Array.isArray(systemConnections)) {
      systemConnections.forEach((connection: any) => {
        const edgeId = `e-${connection.source}-${connection.target}`;
        if (!edges.find((edge) => edge.id === edgeId)) {
          edges.push({
            id: edgeId,
            source: connection.source.toString(),
            target: connection.target.toString(),
            type: 'smoothstep',
            animated: true,
            label: 'depends on',
            style: { stroke: '#2196f3', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#2196f3',
              width: 20,
              height: 20,
            },
          });
        }
      });
    }

    return { nodes, edges };
  }, [experts, displaySystem, isDark]);

  const { nodes, edges } = buildFlowData();
  const fullscreenToggleLabel = isCanvasFullscreen ? 'Exit fullscreen canvas' : 'Fullscreen canvas';
  const fullscreenControl = (
    <Tooltip title={fullscreenToggleLabel}>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        aria-label={fullscreenToggleLabel}
        onClick={() => setIsCanvasFullscreen((current) => !current)}
        startIcon={isCanvasFullscreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
        sx={{
          minHeight: 30,
          whiteSpace: 'nowrap',
          px: 1.25,
        }}
      >
        {isCanvasFullscreen ? 'Exit' : 'Fullscreen'}
      </Button>
    </Tooltip>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        margin: 0,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradient 15s ease infinite',
        '@keyframes gradient': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: { xs: 40, sm: 48 },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              minWidth: { xs: 80, sm: 120 },
              minHeight: { xs: 40, sm: 48 },
              py: { xs: 0.5, sm: 1 },
              textTransform: 'none',
              borderBottom: '3px solid transparent',
              '&.Mui-selected': {
                borderBottom: '3px solid',
                borderBottomColor: 'primary.main',
              },
            },
          }}
        >
          <Tab icon={<AccountTreeIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label="Workflow" />
          <Tab icon={<CalendarIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label={isMobile ? 'System Schedules' : 'AI System Schedules'} />
          <Tab icon={<ScheduleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label="Agent Schedules" />
          <Tab icon={<CalendarIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} iconPosition="start" label={isMobile ? 'Runs' : 'Agent Schedule Executions'} />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: 'background.paper', minWidth: 0, width: '100%' }}>
        {builderPlanStatusView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 1.5 } }}>
            <Alert
              severity={builderPlanStatusView.tone}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {builderPlanStatusView.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {builderPlanStatusView.body}
              </Typography>
              {builderPlanStatusView.chips.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                  {builderPlanStatusView.chips.map((chip) => (
                    <Box
                      key={chip}
                      sx={{
                        px: 1,
                        py: 0.35,
                        borderRadius: 999,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                      }}
                    >
                      {chip}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Alert>
          </Box>
        ) : null}
        {builderPlanDetailView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                px: { xs: 1.25, sm: 1.5 },
                py: 1.25,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {builderPlanDetailView.title}
              </Typography>
              <Box sx={{ display: 'grid', rowGap: 0.9 }}>
                {builderPlanDetailView.rows.map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)' },
                      columnGap: 1.5,
                      rowGap: 0.25,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Box>
              {builderPlanDetailView.note ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  {builderPlanDetailView.note}
                </Typography>
              ) : null}
            </Paper>
          </Box>
        ) : null}
        {builderPlanReviewView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {builderPlanReviewView.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    A quick packet-first checklist for this saved build plan.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                {builderPlanReviewView.focusSummary ? (
                  <Alert
                    severity={builderPlanReviewView.focusTone}
                    variant="outlined"
                    sx={{
                      mb: 1.25,
                      borderRadius: 1.25,
                    }}
                  >
                    <Typography variant="body2">{builderPlanReviewView.focusSummary}</Typography>
                  </Alert>
                ) : null}
                {builderPlanReviewView.summaryChips.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                    {builderPlanReviewView.summaryChips.map((chip) => (
                      <Box
                        key={chip}
                        sx={{
                          px: 1,
                          py: 0.45,
                          borderRadius: 999,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {chip}
                      </Box>
                    ))}
                  </Box>
                ) : null}
                <Box sx={{ display: 'grid', rowGap: 1 }}>
                  {builderPlanReviewView.checklist.map((item) => {
                    const palette =
                      item.status === 'done'
                        ? {
                            bg: 'success.light',
                            color: 'success.dark',
                            label: 'Done',
                          }
                        : item.status === 'current'
                          ? {
                              bg: 'info.light',
                              color: 'info.dark',
                              label: 'Current',
                            }
                          : item.status === 'blocked'
                            ? {
                                bg: 'warning.light',
                                color: 'warning.dark',
                                label: 'Blocked',
                              }
                            : {
                                bg: 'action.hover',
                                color: 'text.secondary',
                                label: 'Pending',
                              };

                    return (
                      <Box
                        key={item.label}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1.25,
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                          <Box
                            sx={{
                              px: 0.9,
                              py: 0.25,
                              borderRadius: 999,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              bgcolor: palette.bg,
                              color: palette.color,
                            }}
                          >
                            {palette.label}
                          </Box>
                        </Box>
                        {item.detail ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {item.detail}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    mt: 1.25,
                    px: 1.25,
                    py: 1,
                    borderRadius: 1.25,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Next step
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.35 }}>
                    {builderPlanReviewView.nextStep}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
        {builderPlanActionView && onUseSuggestedReply ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <BuilderSuggestedReplyCard
              actionView={builderPlanActionView}
              onUseSuggestedReply={onUseSuggestedReply}
            />
          </Box>
        ) : null}
        {builderPlanPreviewView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {builderPlanPreviewView.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The actual saved packet-first changes the builder is holding onto.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                {builderPlanPreviewView.summary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    {builderPlanPreviewView.summary}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                  {builderPlanPreviewView.sections.map((section) => (
                    <Box
                      key={section.title}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {section.items.map((item) => (
                          <Typography
                            key={item}
                            component="li"
                            variant="body2"
                            sx={{ mt: 0.45 }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
        {builderPlanHealthView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {builderPlanHealthView.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Validation, repair, and verification evidence for this saved build plan.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                {builderPlanHealthView.summary ? (
                  <Alert
                    severity={builderPlanHealthView.tone}
                    variant="outlined"
                    sx={{
                      mb: 1.25,
                      borderRadius: 1.25,
                    }}
                  >
                    <Typography variant="body2">{builderPlanHealthView.summary}</Typography>
                  </Alert>
                ) : null}
                <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                  {builderPlanHealthView.sections.map((section) => (
                    <Box
                      key={section.title}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {section.items.map((item) => (
                          <Typography
                            key={item}
                            component="li"
                            variant="body2"
                            sx={{ mt: 0.45 }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
        {builderPlanDiffView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {builderPlanDiffView.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Field-level differences between the original saved draft and the current packet-first plan.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                {builderPlanDiffView.summary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    {builderPlanDiffView.summary}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                  {builderPlanDiffView.sections.map((section) => (
                    <Box
                      key={section.title}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {section.items.map((item) => (
                          <Typography
                            key={item}
                            component="li"
                            variant="body2"
                            sx={{ mt: 0.45 }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
        {builderPlanRevisionView ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1.5,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {builderPlanRevisionView.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    How the saved draft was revised before this packet-first plan became reviewable.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                {builderPlanRevisionView.summary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    {builderPlanRevisionView.summary}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                  {builderPlanRevisionView.sections.map((section) => (
                    <Box
                      key={section.title}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {section.items.map((item) => (
                          <Typography
                            key={item}
                            component="li"
                            variant="body2"
                            sx={{ mt: 0.45 }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
        <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              px: { xs: 1.25, sm: 1.5 },
              py: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Intent ledger
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  User-owned requirements captured for this build.
                </Typography>
              </Box>
              {builderIntentLedger ? (
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  v{builderIntentLedger.ledger_version}
                </Box>
              ) : null}
            </Box>
            {builderIntentLedgerNotice ? (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1, py: 0.25 }}>
                <Typography variant="caption">
                  {builderIntentLedgerNotice}
                </Typography>
              </Alert>
            ) : null}
            {clarificationCards.length > 0 ? (
              <Box sx={{ display: 'grid', rowGap: 0.75, mb: 1 }}>
                {clarificationCards.map((card) => {
                  const isEditingClarification = editingClarificationId === card.clarification_id;
                  const isUpdatingClarification = updatingBuilderIntentItemId === card.clarification_id;
                  return (
                    <Box
                      key={card.clarification_id}
                      sx={{
                        border: 1,
                        borderColor: 'warning.light',
                        borderRadius: 1.25,
                        bgcolor: 'warning.50',
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Clarify {card.term_label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {card.prompt}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.65, fontWeight: 650 }}>
                        Default: {card.default_definition}
                      </Typography>
                      {isEditingClarification ? (
                        <Box sx={{ display: 'grid', rowGap: 0.75, mt: 0.75 }}>
                          <TextField
                            size="small"
                            label="Definition"
                            value={clarificationEditDraft}
                            onChange={(event) => setClarificationEditDraft(event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            required
                            disabled={isUpdatingClarification}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Button size="small" onClick={cancelClarificationEdit} disabled={isUpdatingClarification}>
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => submitClarificationEdit(card)}
                              disabled={isUpdatingClarification || !clarificationEditDraft.trim()}
                            >
                              Save definition
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => acceptClarificationCard(card)}
                            disabled={Boolean(updatingBuilderIntentItemId)}
                          >
                            Accept default
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => startEditingClarificationCard(card)}
                            disabled={Boolean(updatingBuilderIntentItemId)}
                          >
                            Edit definition
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => rejectClarificationCard(card)}
                            disabled={Boolean(updatingBuilderIntentItemId)}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : null}
            {isLoadingBuilderIntentLedger ? (
              <Typography variant="body2" color="text.secondary">
                Loading ledger...
              </Typography>
            ) : !builderIntentLedger ? (
              <Typography variant="body2" color="text.secondary">
                No active ledger is linked yet. New requirements will appear here once capture starts.
              </Typography>
            ) : activeIntentItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This ledger is active, but it has no visible requirements yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                {activeIntentItems.map((item, itemIndex) => {
                  const statusLabel = item.status ? item.status.replace(/_/g, ' ') : 'draft';
                  const isEditing = editingIntentItemId === item.id;
                  const isSplitting = splittingIntentItemId === item.id;
                  const isConfirmingRemoval = confirmingRemovalIntentItemId === item.id;
                  const isUpdating = updatingBuilderIntentItemId === item.id;
                  const canEdit = Boolean(onUpdateBuilderIntentItem);
                  const canReshape = Boolean(onAppendBuilderIntentLedgerEvent);
                  const isLedgerActionInProgress = Boolean(updatingBuilderIntentItemId);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      {isEditing ? (
                        <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                          <TextField
                            size="small"
                            label="Requirement"
                            value={intentEditDraft.label}
                            onChange={(event) => updateIntentEditDraft('label', event.target.value)}
                            fullWidth
                            required
                            disabled={isUpdating}
                          />
                          <TextField
                            size="small"
                            label="Pinned definition"
                            value={intentEditDraft.pinned_definition}
                            onChange={(event) => updateIntentEditDraft('pinned_definition', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={isUpdating}
                          />
                          <TextField
                            size="small"
                            label="Description"
                            value={intentEditDraft.description}
                            onChange={(event) => updateIntentEditDraft('description', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={isUpdating}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Cancel edit">
                              <span>
                                <IconButton size="small" aria-label="Cancel intent edit" onClick={cancelIntentEdit} disabled={isUpdating}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Save intent item">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Save intent item"
                                  color="primary"
                                  onClick={submitIntentEdit}
                                  disabled={isUpdating || !intentEditDraft.label.trim()}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : isSplitting ? (
                        <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                          <TextField
                            size="small"
                            label="First requirement"
                            value={intentSplitDraft.firstLabel}
                            onChange={(event) => updateIntentSplitDraft('firstLabel', event.target.value)}
                            fullWidth
                            required
                            disabled={isUpdating}
                          />
                          <TextField
                            size="small"
                            label="First description"
                            value={intentSplitDraft.firstDescription}
                            onChange={(event) => updateIntentSplitDraft('firstDescription', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={isUpdating}
                          />
                          <TextField
                            size="small"
                            label="Second requirement"
                            value={intentSplitDraft.secondLabel}
                            onChange={(event) => updateIntentSplitDraft('secondLabel', event.target.value)}
                            fullWidth
                            required
                            disabled={isUpdating}
                          />
                          <TextField
                            size="small"
                            label="Second description"
                            value={intentSplitDraft.secondDescription}
                            onChange={(event) => updateIntentSplitDraft('secondDescription', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={isUpdating}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Cancel split">
                              <span>
                                <IconButton size="small" aria-label="Cancel intent split" onClick={cancelIntentSplit} disabled={isUpdating}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Split intent item">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Confirm intent split"
                                  color="primary"
                                  onClick={submitIntentSplit}
                                  disabled={isUpdating || !intentSplitDraft.firstLabel.trim() || !intentSplitDraft.secondLabel.trim()}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : isConfirmingRemoval ? (
                        <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 650 }}>
                            Remove {item.stable_key ? `${item.stable_key} ` : ''}{item.label || 'this requirement'}?
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            This creates a confirmed removal event in the intent ledger.
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Cancel removal">
                              <span>
                                <IconButton size="small" aria-label="Cancel intent removal" onClick={cancelIntentRemoval} disabled={isUpdating}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Confirm removal">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Confirm intent removal"
                                  color="error"
                                  onClick={() => removeIntentItem(item.id)}
                                  disabled={isUpdating}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                                {item.stable_key ? `${item.stable_key} ` : ''}{item.label || 'Untitled requirement'}
                              </Typography>
                              {item.pinned_definition || item.description ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                  {item.pinned_definition || item.description}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <Box
                                sx={{
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: 999,
                                  bgcolor: item.status === 'built' ? 'success.light' : 'action.hover',
                                  color: item.status === 'built' ? 'success.dark' : 'text.secondary',
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {statusLabel}
                              </Box>
                              {canEdit ? (
                                <Tooltip title="Edit intent item">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Edit intent item"
                                      onClick={() => startEditingIntentItem(item)}
                                      disabled={Boolean(updatingBuilderIntentItemId)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {canReshape ? (
                                <>
                                  <Tooltip title="Move up">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Move intent item up"
                                        onClick={() => reorderIntentItem(item.id, -1)}
                                        disabled={isLedgerActionInProgress || itemIndex === 0}
                                      >
                                        <KeyboardArrowUpIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Move down">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Move intent item down"
                                        onClick={() => reorderIntentItem(item.id, 1)}
                                        disabled={isLedgerActionInProgress || itemIndex === activeIntentItems.length - 1}
                                      >
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Split item">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Split intent item"
                                        onClick={() => startSplittingIntentItem(item)}
                                        disabled={isLedgerActionInProgress}
                                      >
                                        <SplitIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Merge with next item">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Merge intent item with next"
                                        onClick={() => mergeIntentItemWithNext(itemIndex)}
                                        disabled={isLedgerActionInProgress || itemIndex === activeIntentItems.length - 1}
                                      >
                                        <MergeIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Remove item">
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Remove intent item"
                                        color="error"
                                        onClick={() => startConfirmingIntentRemoval(item.id)}
                                        disabled={isLedgerActionInProgress}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              ) : null}
                            </Box>
                          </Box>
                          {item.source_excerpt ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="caption" color="text.secondary">
                                From chat: {item.source_excerpt}
                              </Typography>
                              {item.source_message_id && onSelectSourceMessage ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<SourceMessageIcon fontSize="small" />}
                                  onClick={() => onSelectSourceMessage(item.source_message_id as string)}
                                  sx={{ minHeight: 0, px: 0.75, py: 0.25, fontSize: '0.7rem' }}
                                >
                                  Source
                                </Button>
                              ) : null}
                            </Box>
                          ) : null}
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
        {(isLoadingActiveBuilderArtifact || activeBuilderArtifact || artifactReviewRows.length > 0) ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            {architectDesignPackage ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  px: { xs: 1.25, sm: 1.5 },
                  py: 1.25,
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Plan review
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Review the proposed system before building.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isStaff ? (
                      <Tooltip title={showArtifactTechnicalDetails ? 'Hide technical details' : 'Show technical details'}>
                        <IconButton
                          size="small"
                          aria-label={showArtifactTechnicalDetails ? 'Hide technical details' : 'Show technical details'}
                          onClick={() => setShowArtifactTechnicalDetails((current) => !current)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {isStaff && showArtifactTechnicalDetails && architectDesignMode ? (
                      <Box
                        sx={{
                          px: 0.85,
                          py: 0.25,
                          borderRadius: 999,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatReviewToken(architectDesignMode)}
                      </Box>
                    ) : null}
                    {isStaff && showArtifactTechnicalDetails && architectDesignSource ? (
                      <Box
                        sx={{
                          px: 0.85,
                          py: 0.25,
                          borderRadius: 999,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatReviewToken(architectDesignSource)}
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                {architectObjective ? (
                  <Alert severity="info" variant="outlined" sx={{ mb: 1, borderRadius: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {architectObjective}
                    </Typography>
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                  <Box
                    sx={{
                      px: 0.9,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}
                  >
                    {architectExpertDesigns.length} expert design{architectExpertDesigns.length === 1 ? '' : 's'}
                  </Box>
                  {isStaff && showArtifactTechnicalDetails && architectRequestedMutations.length ? (
                    <Box
                      sx={{
                        px: 0.9,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                      }}
                    >
                      {architectRequestedMutations.length} requested mutation{architectRequestedMutations.length === 1 ? '' : 's'}
                    </Box>
                  ) : null}
                  {architectOpenQuestions.length ? (
                    <Box
                      sx={{
                        px: 0.9,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: 'warning.light',
                        color: 'warning.dark',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                      }}
                    >
                      {architectOpenQuestions.length} open question{architectOpenQuestions.length === 1 ? '' : 's'}
                    </Box>
                  ) : null}
                </Box>

                <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                  <Box
                    sx={{
                      display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.25,
                      px: 1,
                      py: 0.85,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Scope
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35 }}>
                      {architectScopeSummary}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.25,
                      px: 1,
                      py: 0.85,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Build brief
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35 }}>
                      {architectBuildBriefSummary}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.25,
                      px: 1,
                      py: 0.85,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Mutation plan
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35 }}>
                      {architectMutationPlanSummary}
                    </Typography>
                  </Box>

                  {architectRequestedMutations.length ? (
                    <Box
                      sx={{
                        display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Requested mutations
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {architectRequestedMutations.map((item) => (
                          <Typography key={item} component="li" variant="body2" sx={{ mt: 0.35 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ) : null}

                  {architectUserConstraints.length ? (
                    <Box
                      sx={{
                        display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        User constraints
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {architectUserConstraints.map((item) => (
                          <Typography key={item} component="li" variant="body2" sx={{ mt: 0.35 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ) : null}

                  {architectOpenQuestions.length ? (
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'warning.main',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
                        Open questions
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.65, mb: 0, pl: 2 }}>
                        {architectOpenQuestions.map((item) => (
                          <Typography key={item} component="li" variant="body2" sx={{ mt: 0.35 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.85 }}>
                        Answer all questions in one reply. The revised plan will replace this version.
                      </Typography>
                      {onUseSuggestedReply ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<SourceMessageIcon />}
                          onClick={() => onUseSuggestedReply(architectOpenQuestionReply)}
                          sx={{ mt: 0.85 }}
                        >
                          Answer all
                        </Button>
                      ) : null}
                    </Box>
                  ) : null}

                  {architectExpertDesigns.length ? (
                    <Box
                      sx={{
                        display: isStaff && showArtifactTechnicalDetails ? 'block' : 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Expert designs
                      </Typography>
                      <Box sx={{ display: 'grid', rowGap: 0.75, mt: 0.75 }}>
                        {architectExpertDesigns.map((design, index) => {
                          const expertName = normalizeArchitectText(design.expert_name || design.name) || `Expert ${index + 1}`;
                          const capabilityKeys = toArchitectTextList(design.capability_keys);
                          const promptSummary = summarizeArchitectExpertPrompt(design);

                          return (
                            <Box
                              key={`${expertName}-${index}`}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                px: 1,
                                py: 0.85,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                                {expertName}
                              </Typography>
                              {capabilityKeys.length ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  Capability keys: {capabilityKeys.join(', ')}
                                </Typography>
                              ) : null}
                              {promptSummary ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                  {promptSummary}
                                </Typography>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Paper>
            ) : null}
            {isStaff && showArtifactTechnicalDetails && durableGraphBuild ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  px: { xs: 1.25, sm: 1.5 },
                  py: 1.25,
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Durable graph build
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {durableGraphBuild.has_linked_run
                        ? `Workflow ${String(durableGraphBuild.workflow_run_id || '').slice(0, 8)}`
                        : 'No linked workflow'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Box
                      sx={{
                        px: 0.85,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: durableGraphBuild.workflow_status === 'COMPLETED' ? 'success.light' : 'action.hover',
                        color: durableGraphBuild.workflow_status === 'COMPLETED' ? 'success.dark' : 'text.secondary',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatWorkflowStatusLabel(durableGraphBuild.workflow_status || 'not_started')}
                    </Box>
                    <Box
                      sx={{
                        px: 0.85,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: durableGraphBuild.validation_status === 'passed' ? 'success.light' : 'action.hover',
                        color: durableGraphBuild.validation_status === 'passed' ? 'success.dark' : 'text.secondary',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatReviewToken(durableGraphBuild.validation_status || 'not_validated')}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', rowGap: 0.65 }}>
                  <Typography variant="caption" color="text.secondary">
                    Current node: {formatReviewToken(durableGraphBuild.current_node_key || 'not_started')}
                  </Typography>
                  {durableGraphBuild.target_ai_system_id ? (
                    <Typography variant="caption" color="text.secondary">
                      Target system: {durableGraphBuild.target_ai_system_id}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    Wrapper tools: {durableWrapperTools.length ? durableWrapperTools.map((tool) => formatReviewToken(tool)).join(', ') : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Readback mismatches: {Number(durableGraphBuild.readback_mismatch_count || 0)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {durableGraphBuild.admin_workflow_url ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      component="a"
                      href={durableGraphBuild.admin_workflow_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open workflow
                    </Button>
                  ) : null}
                  {durableGraphBuild.execute_all_available && durableGraphBuild.admin_execute_all_url ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<AccountTreeIcon fontSize="small" />}
                      component="a"
                      href={durableGraphBuild.admin_execute_all_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Run execute-all
                    </Button>
                  ) : null}
                </Box>
              </Paper>
            ) : null}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                px: { xs: 1.25, sm: 1.5 },
                py: 1.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Requirement review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Artifact-owned status for each captured requirement.
                  </Typography>
                  {artifactWorkflowMode ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      Mode: {formatReviewToken(artifactWorkflowMode)}
                    </Typography>
                  ) : null}
                </Box>
                {isStaff && !architectDesignPackage ? (
                  <Tooltip title={showArtifactTechnicalDetails ? 'Hide technical details' : 'Show technical details'}>
                    <IconButton
                      size="small"
                      aria-label={showArtifactTechnicalDetails ? 'Hide technical details' : 'Show technical details'}
                      onClick={() => setShowArtifactTechnicalDetails((current) => !current)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : activeBuilderArtifact ? (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatReviewToken(activeBuilderArtifact.status)}
                  </Box>
                ) : null}
              </Box>
              {isLoadingActiveBuilderArtifact && artifactReviewRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Loading review...
                </Typography>
              ) : (
                <>
                  {artifactReviewSummary ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {artifactReviewSummary}
                    </Typography>
                  ) : null}
                  {activeBuilderArtifact ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: artifactReviewRows.length > 0 ? 1 : 0 }}>
                      {onApproveActiveBuilderArtifact ? (
                        <Tooltip title={hasUnresolvedArchitectQuestions ? 'Answer the open Architect questions first' : !artifactApproveAction.allowed && artifactApproveAction.reason ? formatReviewToken(artifactApproveAction.reason) : ''}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => onApproveActiveBuilderArtifact()}
                              disabled={hasUnresolvedArchitectQuestions || !artifactApproveAction.allowed || activeBuilderArtifactAction !== null}
                            >
                              {activeBuilderArtifactAction === 'approve' ? approvingArtifactLabel : approveArtifactLabel}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {onApplyActiveBuilderArtifact ? (
                        <Tooltip title={hasUnresolvedArchitectQuestions ? 'Answer the open Architect questions first' : !artifactApplyAction.allowed && artifactApplyAction.reason ? formatReviewToken(artifactApplyAction.reason) : ''}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => onApplyActiveBuilderArtifact()}
                              disabled={hasUnresolvedArchitectQuestions || !artifactApplyAction.allowed || activeBuilderArtifactAction !== null}
                            >
                              {activeBuilderArtifactAction === 'apply' ? 'Applying...' : applyArtifactLabel}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : null}
                  <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                    {visibleArtifactReviewRows.map((row, rowIndex) => {
                      const status = String(row.status || 'unknown');
                      const impact = String(row.approval_impact || '');
                      const isBlocking = impact === 'blocks_approval' || status === 'blocked' || status === 'missing' || status === 'unsupported';
                      const isWarning = !isBlocking && (impact === 'visible_warning' || status === 'weak' || status === 'deferred');
                      return (
                        <Box
                          key={`${row.intent_item_id || row.stable_key || rowIndex}`}
                          sx={{
                            border: 1,
                            borderColor: isBlocking ? 'warning.main' : 'divider',
                            borderRadius: 1.25,
                            px: 1,
                            py: 0.85,
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                                {row.stable_key ? `${row.stable_key} ` : ''}{row.human_label || row.capability_key || 'Requirement'}
                              </Typography>
                              {row.reason ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                  {row.reason}
                                </Typography>
                              ) : null}
                              {row.required_user_action ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                  Action: {row.required_user_action}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <Box
                                sx={{
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: 999,
                                  bgcolor: isBlocking ? 'warning.light' : isWarning ? 'info.light' : 'success.light',
                                  color: isBlocking ? 'warning.dark' : isWarning ? 'info.dark' : 'success.dark',
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatReviewToken(status)}
                              </Box>
                              {row.implementation_mode ? (
                                <Box
                                  sx={{
                                    px: 0.8,
                                    py: 0.25,
                                    borderRadius: 999,
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatReviewToken(row.implementation_mode)}
                                </Box>
                              ) : null}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        ) : null}
        {isStaff && showArtifactTechnicalDetails && activeBuilderArtifact ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                px: { xs: 1.25, sm: 1.5 },
                py: 1.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Runtime status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Latest workflow node evidence for this artifact.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {Number(runtimeSummary.node_count || 0)} nodes
                </Box>
              </Box>
              {runtimeRuns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No runtime evidence recorded yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                  {runtimeRuns.map((run, runIndex) => {
                    const statusText = String(run.status || 'UNKNOWN');
                    const freshnessText = String(run.freshness_status || 'unknown');
                    const isBlocked = statusText === 'BLOCKED' || statusText === 'FAILED';
                    const evidence = summarizeRuntimeSnapshot(run.decision_snapshot, summarizeRuntimeSnapshot(run.output_snapshot, 'No output evidence'));
                    const costSnapshot = (run.cost_snapshot ?? {}) as Record<string, any>;
                    const creditSnapshot = {
                      ...(costSnapshot.credits || costSnapshot.credit_usage || costSnapshot.credits_used
                        ? { credits: costSnapshot.credits ?? costSnapshot.credit_usage ?? costSnapshot.credits_used }
                        : {}),
                      ...(costSnapshot.tokens ? { tokens: costSnapshot.tokens } : {}),
                    };
                    const costText = Object.keys(creditSnapshot).length > 0
                      ? summarizeRuntimeSnapshot(creditSnapshot, 'Credits captured')
                      : '';
                    return (
                      <Box
                        key={`${run.node_key || run.id || runIndex}`}
                        sx={{
                          border: 1,
                          borderColor: isBlocked ? 'warning.main' : 'divider',
                          borderRadius: 1.25,
                          px: 1,
                          py: 0.85,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 650 }}>
                              {formatReviewToken(run.node_key || run.operation_id || 'Workflow node')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              {evidence}
                            </Typography>
                            {run.blocking_reason ? (
                              <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.35 }}>
                                Blocked: {formatReviewToken(run.blocking_reason)}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              {[costText, run.latency_ms ? `${run.latency_ms} ms` : '', run.completed_at || run.created_at ? formatStoredDateTime(run.completed_at || run.created_at) : '']
                                .filter(Boolean)
                                .join(' | ')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Box
                              sx={{
                                px: 0.8,
                                py: 0.25,
                                borderRadius: 999,
                                bgcolor: isBlocked ? 'warning.light' : 'success.light',
                                color: isBlocked ? 'warning.dark' : 'success.dark',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatReviewToken(statusText)}
                            </Box>
                            <Box
                              sx={{
                                px: 0.8,
                                py: 0.25,
                                borderRadius: 999,
                                bgcolor: freshnessText === 'stale' ? 'warning.light' : 'action.hover',
                                color: freshnessText === 'stale' ? 'warning.dark' : 'text.secondary',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatReviewToken(freshnessText)}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Box>
        ) : null}
        {refinementProposals.length > 0 ? (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                px: { xs: 1.25, sm: 1.5 },
                py: 1.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Refinement review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Proposed changes from performance evidence before approval.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {Number(refinementSummary.proposal_count || refinementProposals.length)} proposals
                </Box>
              </Box>
              <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                {refinementProposals.map((proposal, proposalIndex) => {
                  const candidateChanges = Array.isArray(proposal.candidate_changes) ? proposal.candidate_changes : [];
                  const replayCheck = (proposal.backtest_replay_check ?? {}) as Record<string, any>;
                  const tradeSummary = (proposal.trade_history_summary ?? {}) as Record<string, any>;
                  const beforeAfterEvidence = (proposal.before_after_evidence ?? {}) as Record<string, any>;
                  const baselineMetrics = (beforeAfterEvidence.baseline_metrics ?? {}) as Record<string, any>;
                  return (
                    <Box
                      key={`${proposal.id || proposalIndex}`}
                      sx={{
                        border: 1,
                        borderColor: replayCheck.required_before_approval ? 'warning.main' : 'divider',
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.85,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 650 }}>
                            {proposal.summary || formatReviewToken(proposal.proposal_type || 'Refinement proposal')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                            {summarizeRuntimeSnapshot(tradeSummary, 'Trade history evidence captured')}
                          </Typography>
                          {Object.keys(baselineMetrics).length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              Baseline: {summarizeRuntimeSnapshot(baselineMetrics, 'Baseline metrics captured')}
                            </Typography>
                          ) : null}
                          {replayCheck.required_before_approval ? (
                            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.35 }}>
                              Replay check required before approval{replayCheck.status ? `: ${formatReviewToken(replayCheck.status)}` : ''}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            px: 0.8,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatReviewToken(proposal.status || 'Awaiting review')}
                        </Box>
                      </Box>
                      {candidateChanges.length > 0 ? (
                        <Box component="ul" sx={{ mt: 0.75, mb: 0, pl: 2 }}>
                          {candidateChanges.slice(0, 5).map((change: any, changeIndex: number) => (
                            <Typography key={`${change.label || changeIndex}`} component="li" variant="caption" sx={{ display: 'list-item', mt: 0.35 }}>
                              {change.label || change.description || 'Review candidate change'}{change.description && change.label ? `: ${change.description}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        ) : null}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: { xs: 1, sm: 2 } }}>
            {shouldUseSnapshot && (
              <Alert
                severity="info"
                icon={<AccessTimeIcon />}
                sx={{
                  mb: 2,
                  py: 0.5,
                  '& .MuiAlert-message': { py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' },
                }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => {
                      setForceLiveView(true);
                      onExitSnapshot?.();
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography variant="caption">
                  Viewing snapshot from <strong>{formatStoredDateTime(sessionData.system_snapshot.timestamp, undefined, '-')}</strong>
                </Typography>
              </Alert>
            )}

            <Portal disablePortal={!isCanvasFullscreen}>
              <Paper
                elevation={3}
                sx={{
                  flexGrow: 1,
                  position: isCanvasFullscreen ? 'fixed' : 'relative',
                  inset: isCanvasFullscreen ? 0 : 'auto',
                  zIndex: isCanvasFullscreen ? 2147483000 : 'auto',
                  width: isCanvasFullscreen ? '100vw' : 'auto',
                  maxWidth: isCanvasFullscreen ? '100vw' : 'none',
                  minHeight: isCanvasFullscreen ? '100dvh' : 500,
                  height: isCanvasFullscreen ? '100dvh' : canUseInteractiveCanvas ? 680 : canvasNodes.length > 0 ? 600 : 500,
                  maxHeight: isCanvasFullscreen ? '100dvh' : 'none',
                  m: 0,
                  borderRadius: isCanvasFullscreen ? 0 : 2,
                  overflow: 'hidden',
                  bgcolor: isDark ? '#1a1a1a' : 'background.paper',
                  boxShadow: isCanvasFullscreen ? 24 : undefined,
                }}
              >
              {!canUseInteractiveCanvas && (
                <Box className="nodrag nopan" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 20 }}>
                  {fullscreenControl}
                </Box>
              )}

              {canUseInteractiveCanvas ? (
                <ReactFlowProvider>
                  <Box sx={{ width: '100%', height: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
                    <CanvasResizeFitView isFullscreen={isCanvasFullscreen} nodeCount={canvasNodes.length} />
                    <AISystemCanvas
                      nodes={canvasNodes}
                      edges={canvasEdges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      onNodeClick={onNodeClick!}
                      onAutoLayout={onAutoLayout!}
                      expertsData={canvasExpertsData}
                      addExpertToCanvas={addExpertToCanvas!}
                      setExpertFormOpen={setExpertFormOpen!}
                      selectedNode={selectedNode ?? null}
                      setSelectedNode={setSelectedNode!}
                      setNodes={setNodes!}
                      setEdges={setEdges!}
                      refetchExperts={refetchExperts!}
                      validationIssues={validationIssues}
                      onSaveCanvas={onSaveCanvas}
                      isSavingCanvas={isSavingCanvas}
                      onRunCanvas={onRunCanvas}
                      isRunningCanvas={isRunningCanvas}
                      canRunCanvas={canRunCanvas}
                      runDisabledReason={runDisabledReason}
                      expertRunStates={expertRunStates}
                      onViewExpertRun={onViewExpertRun}
                      toolbarActions={fullscreenControl}
                      isMobile={isMobile}
                      isDark={isDark}
                    />
                  </Box>
                </ReactFlowProvider>
              ) : experts.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <ReactFlowProvider>
                    <CanvasResizeFitView isFullscreen={isCanvasFullscreen} nodeCount={nodes.length} />
                    <ReactFlow
                      nodes={nodes.map((node) => ({
                        ...node,
                        data: { ...node.data, isDark },
                      }))}
                      edges={edges}
                      fitView
                      attributionPosition="bottom-left"
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={false}
                      nodesFocusable={false}
                      edgesFocusable={false}
                      panOnScroll={false}
                      zoomOnScroll
                      zoomOnPinch
                    >
                      <Background color={isDark ? '#444' : '#e0e0e0'} />
                      <Controls
                        showInteractive={false}
                        style={{
                          backgroundColor: isDark ? '#2a2a2a' : undefined,
                          borderColor: isDark ? '#555' : undefined,
                          boxShadow: isDark ? '0 0 4px rgba(0,0,0,0.5)' : undefined,
                        }}
                      />
                      {!isMobile && (
                        <MiniMap
                          nodeColor={(node: Node) => node.style?.background as string || (isDark ? '#444' : '#e0e0e0')}
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#f0f0f0' }}
                          maskColor={isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                        />
                      )}
                    </ReactFlow>
                  </ReactFlowProvider>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    p: { xs: 2, sm: 4 },
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary">
                    No Experts Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Create experts to visualize your AI system network
                  </Typography>
                </Box>
              )}
              </Paper>
            </Portal>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <SchedulePanel systemId={system.id} />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <AgentSchedulePanel systemId={system.id} />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ mx: -2, mt: -2 }}>
            <ScheduleExecutionsScreen aiSystemId={system.id} showStatsInitially={false} />
          </Box>
        </TabPanel>
      </Box>
    </Paper>
  );
};

export default AIBuilderInsightsPanel;
